
        // Import navigation components
        import { LeftSidebar } from './js/components/navigation/left-sidebar.js?v=20250930';
        import { TopMenuBar } from './js/components/navigation/top-menubar.js';
        import { NavigationManager } from './js/managers/navigation-manager.js';
        // Import existing components
        import { TableComponent } from './js/components/table.js';
        import { ModalComponent } from './js/components/modal.js';
        import { ChartComponent } from './js/components/chart.js';
        import { TradeDetailsPanel } from './js/components/trade-details/trade-details-panel.js';
        import { Helpers } from './js/utils/helpers.js';
        import { SSEService } from './js/services/sse-service.js';
        // Import user feed components
        import { AssetSelector } from './js/components/widgets/asset-selector.js';
        import { PositionsPanel } from './js/components/widgets/positions-panel.js';
        import { BotStatusMonitor } from './js/components/widgets/bot-status-monitor.js';
        // Removed GlobalPositionsService import
        // Import trading components
        import { TradingChart } from './js/components/trading/trading-chart.js';
        import { TradingTerminal } from './js/components/trading/trading-terminal.js';
        
        // Import Lighter Mode Manager
        import ModeManager from '../lighter/managers/mode-manager.js';
        import LighterCredentialsManager from '../lighter/managers/lighter-credentials-manager.js';
        import LighterClient from '../lighter/managers/lighter-client.js';
        
        import { App } from './js/app.js';
        // Expose classes to global scope for regular scripts
        window.App = App;
        window.LeftSidebarClass = LeftSidebar;
        window.TopMenuBar = TopMenuBar;
        window.NavigationManagerClass = NavigationManager;
        // Expose trading components to global window
        window.TradingChart = TradingChart;
        window.TradingTerminal = TradingTerminal;
        
        // Import and initialize Browser Auto Trade Bot in background (non-blocking)
        import('./js/services/browser-auto-trade-bot.js').then(({ BrowserAutoTradeBot }) => {
            // console.log('🤖 [INIT] BrowserAutoTradeBot class imported successfully');
            
            // Explicitly create singleton if not auto-created
            if (!window.BrowserAutoTradeBot) {
                // console.log('🤖 [INIT] window.BrowserAutoTradeBot not found, creating manually...');
                window.BrowserAutoTradeBot = new BrowserAutoTradeBot();
                // console.log('✅ [INIT] Browser Auto Trade Bot manually instantiated');
            }
        }).catch(error => {
            console.error('❌ [INIT] CRITICAL: Failed to import Browser Auto Trade Bot:', error);
            console.error('❌ [INIT] Error stack:', error.stack);
        });
        
        // Signal that modules are loaded
        window._modulesReady = true;
        window.dispatchEvent(new Event('modulesReady'));
        // console.log('✅ All modules loaded and exposed to window');

        // MM4 Mode Functions
        // Enable MM4 mode: order key/public address from API credentials, cancel key from MM4 settings, Ably via server token
        window.enableMM4 = async (config = {}) => {
            let { orderKey, cancelKey, publicAddress } = config;
            const channelName = config.channelName || 'mm4-signals';

            // Pull order key / address from password manager if not supplied
            if (!orderKey || !publicAddress) {
                const pm = window.PasswordManager;
                if (pm && pm.isUserReady()) {
                    const creds = pm.getApiCredentials() || {};
                    orderKey = orderKey || creds.apiKey;
                    publicAddress = publicAddress || creds.publicAddress;
                }
            }

            if (!orderKey || !cancelKey) {
                console.error('[MM4] Order key and cancel key required');
                return { success: false, error: 'Order key and cancel key required' };
            }

            try {
                console.log('[MM4] Initializing with order/cancel keys...');
                
                // Get remote node configuration from localStorage
                const remoteNodeUrl = localStorage.getItem('remoteNodeUrl');
                const useRemoteNode = localStorage.getItem('useRemoteNode') === 'true';
                
                // Initialize Hyperliquid client first (required by others)
                await window.BrowserHyperliquidClient.initializeDualKey(
                    orderKey,
                    cancelKey,
                    publicAddress,
                    false, // isTestnet
                    useRemoteNode ? remoteNodeUrl : null
                );
                console.log('[MM4] Dual-key HyperliquidClient initialized' + (useRemoteNode ? ' with remote node' : ''));

                // Start all dependent services in PARALLEL for faster initialization
                await Promise.all([
                    // Position tracker
                    (async () => {
                        if (window.BrowserPositionTracker && publicAddress) {
                            await window.BrowserPositionTracker.start(publicAddress);
                            console.log('[MM4] Position tracker started for address:', publicAddress);
                        }
                    })(),
                    
                    // Ably connection (independent)
                    (async () => {
                        await window.AblyService.connect({ channelName });
                        console.log('[MM4] Ably connected to channel:', channelName);
                    })(),
                    
                    // Signal handler (needs HyperliquidClient only)
                    (async () => {
                        await window.MM4SignalHandler.initialize(window.BrowserHyperliquidClient);
                        console.log('[MM4] Signal handler initialized');
                    })()
                ]);
                
                // Initialize SpotHandler AFTER position tracker is ready
                if (window.SpotHandler && !window.SpotHandler.hlClient) {
                    await window.SpotHandler.initialize(
                        window.BrowserHyperliquidClient,
                        window.BrowserPositionTracker
                    );
                    console.log('[MM4] SpotHandler initialized with auto-restart');
                }

                console.log('[MM4] Mode enabled - receiving signals from mm4.py');
                console.log('[MM4] Dual-key mode:', window.BrowserHyperliquidClient?.isDualKeyMode ? 'YES' : 'NO');
                return { success: true, dualKeyMode: window.BrowserHyperliquidClient?.isDualKeyMode };
            } catch (error) {
                console.error('[MM4] Failed to enable:', error);
                return { success: false, error: error.message };
            }
        };

        // Disable MM4 mode
        window.disableMM4 = () => {
            if (window.AblyService) {
                window.AblyService.disconnect();
            }
            if (window.MM4SignalHandler) {
                window.MM4SignalHandler.setEnabled(false);
            }
            console.log('[MM4] Mode disabled');
            return { success: true };
        };

        // Get MM4 stats
        window.getMM4Stats = () => {
            const ablyStatus = window.AblyService ? window.AblyService.getStatus() : null;
            const handlerStats = window.MM4SignalHandler ? window.MM4SignalHandler.getStats() : null;
            return {
                ably: ablyStatus,
                handler: handlerStats
            };
        };

        // Get pending SL/TP orders (for debugging)
        window.getMM4PendingSLTP = () => {
            return window.MM4SignalHandler ? window.MM4SignalHandler.getPendingSLTP() : [];
        };

        // AUTO-CONNECT MM4 on page load (ALWAYS - MM4 is the primary mode now)
        window.autoConnectMM4 = async () => {
            // MM4 mode is now forced - always try to connect if keys are available
            // console.log('[MM4] Auto-connect starting (forced mode)...');

            // Wait for password manager to be ready
            const pm = window.PasswordManager;
            if (!pm || !pm.isUserReady()) {
                console.log('[MM4] Auto-connect waiting for password manager...');
                return;  // Will be called again when password is entered
            }

            try {
                // Load encrypted cancel key + channel
                const encCancelKey = localStorage.getItem('ev_plus_mm4_cancel_key');
                const channelName = localStorage.getItem('ev_plus_mm4_channel') || 'mm4-signals';

                const creds = pm.getApiCredentials() || {};
                const orderKey = creds.apiKey;
                const publicAddress = creds.publicAddress || '';

                if (!orderKey || !encCancelKey) {
                    // console.warn('[MM4] Dual-key setup not complete - falling back to single-key mode');
                    
                    // Fallback: Initialize with single key (regular mode) - NON-BLOCKING
                    if (orderKey && window.orderClient) {
                        window.orderClient.initialize(orderKey, publicAddress).then(() => {
                            // console.log('[FALLBACK] Client initialized successfully');
                            
                            // Start dependent services in parallel
                            return Promise.all([
                                window.BrowserPositionTracker && publicAddress ? 
                                    window.BrowserPositionTracker.start(publicAddress) : Promise.resolve(),
                                window.SpotHandler && !window.SpotHandler.hlClient ? 
                                    window.SpotHandler.initialize(window.BrowserHyperliquidClient, window.BrowserPositionTracker) : Promise.resolve()
                            ]);
                        }).then(() => {
                            console.log('[FALLBACK] Single-key mode ready');
                        }).catch(err => console.error('[FALLBACK] Init error:', err));
                    }
                    return;
                }

                // Decrypt cancel key
                const cancelKey = await pm.decryptData(JSON.parse(encCancelKey));

                if (!cancelKey) {
                    console.warn('[MM4] Auto-connect failed: decryption failed - falling back to single-key mode');
                    
                    // Fallback: Initialize with single key - NON-BLOCKING
                    if (orderKey && window.orderClient) {
                        window.orderClient.initialize(orderKey, publicAddress).then(() => {
                            // console.log('[FALLBACK] Client initialized successfully');
                            
                            // Start dependent services in parallel
                            return Promise.all([
                                window.BrowserPositionTracker && publicAddress ? 
                                    window.BrowserPositionTracker.start(publicAddress) : Promise.resolve(),
                                window.SpotHandler && !window.SpotHandler.hlClient ? 
                                    window.SpotHandler.initialize(window.BrowserHyperliquidClient, window.BrowserPositionTracker) : Promise.resolve()
                            ]);
                        }).then(() => {
                            console.log('[FALLBACK] Single-key mode ready');
                        }).catch(err => console.error('[FALLBACK] Init error:', err));
                    }
                    return;
                }

                // Connect in background - DON'T BLOCK APP LOAD!
                console.log('[MM4] Auto-connecting with dual keys...');
                window.enableMM4({
                    orderKey,
                    cancelKey,
                    publicAddress,
                    channelName
                }).then(() => {
                    // Mark as enabled in localStorage for UI consistency
                    localStorage.setItem('ev_plus_mm4_enabled', 'true');
                    console.log('[MM4] Auto-connected successfully! (dual-key mode)');
                }).catch(err => console.error('[MM4] Connection error:', err));

            } catch (e) {
                console.error('[MM4] Auto-connect error:', e);
                
                // Final fallback: Try single-key initialization - NON-BLOCKING
                const creds = window.PasswordManager?.getApiCredentials();
                if (creds?.apiKey && window.orderClient) {
                    console.log('[FALLBACK] Attempting single-key initialization after error...');
                    window.orderClient.initialize(creds.apiKey, creds.publicAddress).then(() => {
                        if (window.BrowserPositionTracker && creds.publicAddress) {
                            return window.BrowserPositionTracker.start(creds.publicAddress);
                        }
                    }).then(() => {
                        console.log('[FALLBACK] Single-key mode initialized');
                    }).catch(fallbackError => {
                        console.error('[FALLBACK] Single-key initialization also failed:', fallbackError);
                    });
                }
            }
        };

        // Try auto-connect after password manager becomes ready
        // Listen for password unlock event (primary connection method)
        window.addEventListener('passwordUnlocked', () => {
            // console.log('[MM4] Password unlocked, attempting auto-connect (forced)...');
            setTimeout(() => window.autoConnectMM4(), 100);
        });

        // Single fallback check if password is already unlocked on page load
        setTimeout(() => {
            if (window.PasswordManager && window.PasswordManager.isUserReady()) {
                window.autoConnectMM4();
            }
        }, 500);
    